import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useInvoices, useInvoiceStats, useSuppliers } from '@/hooks/useInvoices';
import {
  Receipt,
  Upload,
  Search,
  AlertTriangle,
  Clock,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Link2,
  Loader2,
  DollarSign,
  Star,
} from 'lucide-react';
import { PageHeader } from '@/components/admin';
import { Button, Modal, Card, Spinner } from '@/components/common';
import { DataTable } from '@/components/ui/data-table';
import { type ColumnDef } from '@tanstack/react-table';
import { invoices as invoicesApi } from '@/utils/api';
import { toast } from 'sonner';
import { buildInvoiceRows, getInvoiceSubRows, getInvoiceRowId } from './buchhaltung/useInvoiceRows';
import {
  ReminderStatusCell,
  ReminderNumberCell,
  ReminderSupplierCell,
  ReminderAmountCell,
  ReminderDateCell,
  ReminderDueCell,
  ParentReminderBadge,
} from './buchhaltung/ReminderChildRow';
import ReminderAssignDialog from './buchhaltung/ReminderAssignDialog';
import InvoiceDetailModal from './buchhaltung/InvoiceDetailModal';
import { useInvoiceDetail } from './buchhaltung/useInvoiceDetail';
// Status-Konfiguration, Formatierer und Fach-Praedikate teilen sich Liste und
// Detail-Modal — sie lagen frueher hier lokal und wuerden sonst doppelt gepflegt.
import {
  CATEGORY_OPTIONS,
  formatCurrency,
  formatDate,
  getPaymentDeadline,
  isImportantEligible,
  isReminderDone,
  STATUS_CONFIG,
} from './buchhaltung/invoiceUtils';
import DueDateCell from './buchhaltung/DueDateCell';
import PaymentBatchBar from './buchhaltung/PaymentBatchBar';
import { isPayableRow } from './buchhaltung/paymentBatch';

const Buchhaltung = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Filters — #2/#3: Tabs statt Typ-Dropdown, Default = Eingang (keine Mahnungen)
  const [statusFilter, setStatusFilter] = useState('');
  // Ausgangsrechnungen haben seit Okt. 2026 eine eigene Seite (/admin/ausgangsrechnungen)
  const [activeTab, setActiveTab] = useState('incoming'); // incoming | reminder
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showEmployeeOnly, setShowEmployeeOnly] = useState(false); // #9

  // Upload modal
  const [uploadModal, setUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  // Detail-Modal: Zustand, Mutationen und Breadcrumb-Stapel liegen komplett im Hook.
  const detail = useInvoiceDetail();

  // Expense claim modal
  const [expenseModal, setExpenseModal] = useState(false);
  const [expenseData, setExpenseData] = useState({
    supplier_name: '', description: '', gross_amount: '', invoice_date: new Date().toISOString().split('T')[0],
    category: '', notes: '', employee_name: '',
  });
  const [expenseSaving, setExpenseSaving] = useState(false);
  const expenseFileRef = useRef(null);

  // Eltern-Kind-Zeilen: aufgeklappte RECHNUNGEN zeigen ihre Mahnungen als Kinder.
  // (Bis Juli 2026 umgekehrt — Mahnung als Elternzeile mit Rechnungen als Kindern.)
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  // Mahnungen-Tab: ALLE Mahnungen sind der Normalfall (#121). Wer nur die noch
  // unzugeordneten sehen will, haekelt den Schalter ab — die Wahl bleibt
  // erhalten, damit sie nicht bei jedem Seitenwechsel neu getroffen werden muss.
  const [showAllReminders, setShowAllReminders] = useState(
    () => localStorage.getItem('buchhaltung.showAllReminders') !== '0',
  );
  useEffect(() => {
    localStorage.setItem('buchhaltung.showAllReminders', showAllReminders ? '1' : '0');
  }, [showAllReminders]);
  // Zuordnungs-Dialog (vorher gab es dafuer gar keine Oberflaeche)
  const [assignReminder, setAssignReminder] = useState<any | null>(null);

  // Mehrfachauswahl fuer die Sammelueberweisung. Die Schluessel kommen aus
  // getInvoiceRowId ("i<id>" fuer Rechnungen, "i<id>.r<id>" fuer Mahnungen).
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Build query params
  const invoiceParams: Record<string, any> = {};
  if (statusFilter) invoiceParams.status = statusFilter;
  if (activeTab) invoiceParams.type = activeTab;
  if (debouncedSearch) invoiceParams.search = debouncedSearch;
  // Kind-Mahnungen gleich mitladen — sonst braeuchte jede aufgeklappte Rechnung
  // einen eigenen Request. Im Mahnungen-Tab unnoetig (dort sind die Mahnungen selbst die Zeilen).
  if (activeTab !== 'reminder') invoiceParams.include = 'reminders';
  // Der Mahnungen-Tab ist eine Arbeitsliste: standardmaessig nur Mahnungen OHNE
  // zugeordnete Rechnung. Zugeordnete sieht man unter ihrer Rechnung.
  if (activeTab === 'reminder' && !showAllReminders) invoiceParams.linked = 'none';

  // TanStack Query hooks
  const { data: invoicesList = [], isLoading: loading } = useInvoices(invoiceParams);
  const { data: stats } = useInvoiceStats();
  const { data: supplierList = [] } = useSuppliers();

  const invalidateInvoices = () => queryClient.invalidateQueries({ queryKey: ['invoices'] });

  // Mutations
  const uploadMutation = useMutation({
    mutationFn: (formData: FormData) => invoicesApi.upload(formData),
    onSuccess: (res) => {
      const data = res.data;
      setUploadResult(data);
      if (data.type === 'reminder') {
        toast.success(data.matched
          ? `Mahnung erkannt und automatisch zugeordnet (Rechnung #${data.matched_invoice_id})`
          : 'Mahnung erkannt — bitte manuell zuordnen',
        );
      } else if (data.data?.is_bon) {
        toast.success('Kassenbon erkannt — Vorschlag: als "Bezahlt" markieren');
      } else {
        toast.success('Rechnung erfolgreich hochgeladen und analysiert');
      }
      invalidateInvoices();
    },
    onError: (err: any) => {
      toast.error(err.message || 'Fehler beim Hochladen');
    },
    onSettled: () => {
      setUploading(false);
      if (fileInputRef.current) (fileInputRef.current as HTMLInputElement).value = '';
    },
  });

  // #119 "Wichtige Rechnung" — Stern in der Zeile bzw. Checkbox im Detail-Modal.
  // Markierte Rechnungen stehen in der unsortierten Liste oben (Backend-ORDER BY)
  // und sind farbig hinterlegt; bezahlt faellt die Markierung weg.
  const importantMutation = useMutation({
    mutationFn: ({ invoiceId, important }: { invoiceId: number; important: boolean }) =>
      invoicesApi.setImportant(invoiceId, important),
    onSuccess: (res, { invoiceId, important }) => {
      toast.success(important ? 'Als wichtig markiert' : 'Markierung entfernt');
      // Stern in der Tabelle geklickt, waehrend dieselbe Rechnung offen ist →
      // das Modal muss den neuen Stand mitbekommen.
      if (detail.invoice?.id === invoiceId) void detail.reload();
      invalidateInvoices();
    },
    onError: (err: any) => toast.error(err.response?.data?.error || err.message || 'Fehler beim Markieren'),
  });

  // #130: Sammelüberweisung abschliessen. Die Betraege rechnet der Server —
  // hier gehen nur die IDs und der Skonto-Schalter raus.
  const markPaidMutation = useMutation({
    mutationFn: ({ ids, applySkonto }: { ids: number[]; applySkonto: boolean }) =>
      invoicesApi.markPaidBatch(ids, applySkonto),
    onSuccess: (res: any) => {
      const { markedCount = 0, skipped = [], totalTransferred = 0, totalSkonto = 0 } = res.data || {};
      if (markedCount > 0) {
        toast.success(
          `${markedCount} ${markedCount === 1 ? 'Beleg' : 'Belege'} als bezahlt markiert` +
          ` — ${totalTransferred.toFixed(2).replace('.', ',')} €` +
          (totalSkonto > 0 ? ` (${totalSkonto.toFixed(2).replace('.', ',')} € Skonto gezogen)` : ''),
        );
      }
      // Uebersprungene nicht verschweigen: sonst glaubt die Buchhaltung, alles
      // sei gebucht, waehrend eine Rechnung offen geblieben ist.
      if (skipped.length > 0) {
        toast.warning(
          `${skipped.length} ${skipped.length === 1 ? 'Beleg' : 'Belege'} übersprungen` +
          ` (bereits abgeschlossen oder nicht zahlbar)`,
        );
      }
      setRowSelection({});
      if (detail.invoice) void detail.reload();
      invalidateInvoices();
    },
    onError: (err: any) =>
      toast.error(err.response?.data?.error || err.message || 'Fehler beim Markieren als bezahlt'),
  });

  const expenseMutation = useMutation({
    mutationFn: async (data: { file?: File; fields: Record<string, any> }) => {
      if (data.file) {
        const formData = new FormData();
        formData.append('file', data.file);
        const uploadRes = await invoicesApi.upload(formData);
        await invoicesApi.update(uploadRes.data.id, data.fields);
      } else {
        await invoicesApi.create(data.fields);
      }
    },
    onSuccess: () => {
      toast.success('Ausgabenbeleg erstellt');
      setExpenseModal(false);
      setExpenseData({
        supplier_name: '', description: '', gross_amount: '', invoice_date: new Date().toISOString().split('T')[0],
        category: '', notes: '', employee_name: '',
      });
      if (expenseFileRef.current) (expenseFileRef.current as HTMLInputElement).value = '';
      invalidateInvoices();
    },
    onError: (err: any) => toast.error(err.message || 'Fehler beim Erstellen'),
    onSettled: () => setExpenseSaving(false),
  });

  // Deep-Link: ?invoice=123 → Detail-Modal automatisch öffnen
  useEffect(() => {
    const invoiceId = searchParams.get('invoice');
    if (invoiceId && !detail.invoice) {
      void detail.open(Number(invoiceId));
      // URL-Parameter entfernen nach dem Öffnen
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  // toggleReminderExpand entfaellt: die Kind-Mahnungen kommen mit ?include=reminders
  // bereits in der Listen-Antwort mit — kein Request mehr pro aufgeklappter Zeile.

  // Upload handler
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    const formData = new FormData();
    formData.append('file', file);
    uploadMutation.mutate(formData);
  };

  // Expense claim submit
  const submitExpense = () => {
    if (!expenseData.supplier_name || !expenseData.gross_amount) {
      toast.error('Lieferant und Betrag sind Pflichtfelder');
      return;
    }
    setExpenseSaving(true);
    const gross = parseFloat(expenseData.gross_amount) || 0;
    const netAmount = Math.round(gross / 1.19 * 100) / 100;
    const vatAmount = Math.round((gross - netAmount) * 100) / 100;

    const fields = {
      type: 'expense_claim',
      supplier_name: expenseData.supplier_name,
      description: expenseData.description,
      gross_amount: gross,
      net_amount: netAmount,
      vat_amount: vatAmount,
      invoice_date: expenseData.invoice_date,
      category: expenseData.category || 'Sonstiges',
      notes: expenseData.notes,
      employee_name: expenseData.employee_name,
    };

    const file = expenseFileRef.current?.files?.[0];
    expenseMutation.mutate({ file, fields });
  };

  // Status tabs
  const statusTabs = [
    { value: '', label: 'Alle' },
    { value: 'open', label: 'Offen' },
    { value: 'paid', label: 'Bezahlt' },
    { value: 'clarification', label: 'Klärung' },
  ];

  // Filter logic — #9: Mitarbeiter-Filter
  const filteredInvoices = showEmployeeOnly
    ? invoicesList.filter(inv => !!inv.is_employee_invoice)
    : invoicesList;

  // Eltern-Kind-Zeilen: Rechnung als Elternteil, ihre Mahnungen als echte
  // Sub-Rows (nicht als flach eingeschobene Marker-Zeilen — das bricht sonst
  // Pagination und Sortierung, siehe Kommentar in useInvoiceRows.ts).
  const effectiveRows = useMemo(() => buildInvoiceRows(filteredInvoices), [filteredInvoices]);

  // Markierte Rechnungen fuer die Sammelueberweisung. Bewusst aus den aktuellen
  // Zeilen gefiltert statt aus einem eigenen Zustand: eine Auswahl kann
  // stehenbleiben, nachdem eine Rechnung inzwischen bezahlt wurde oder aus dem
  // Filter gefallen ist — die darf dann nicht mehr in die Summe zaehlen.
  const selectedInvoices = useMemo(
    () => effectiveRows.filter((row: any) => rowSelection[`i${row.id}`] && isPayableRow(row)),
    [effectiveRows, rowSelection],
  );

  // Tab-, Filter- oder Suchwechsel zeigt eine andere Liste — eine Auswahl, die
  // der Nutzer nicht mehr sehen kann, darf nicht unsichtbar weitersummieren.
  useEffect(() => {
    setRowSelection({});
  }, [activeTab, statusFilter, debouncedSearch, showEmployeeOnly]);

  // Column definitions for DataTable
  const columns: ColumnDef<any>[] = useMemo(() => [
    // Auswahlspalte nur dort, wo ueberwiesen wird. Im Ausgangs- und
    // Mahnungen-Tab waere jede Checkbox deaktiviert — dann lieber keine Spalte.
    ...(activeTab === 'incoming' ? [{
      id: 'select',
      size: 44,
      minSize: 44,
      enableResizing: false,
      enableHiding: false,
      enableSorting: false,
      header: ({ table }: any) => (
        <input
          type="checkbox"
          aria-label="Alle auswählen"
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          checked={table.getIsAllPageRowsSelected()}
          ref={(el: HTMLInputElement | null) => {
            if (el) el.indeterminate = table.getIsSomePageRowsSelected() && !table.getIsAllPageRowsSelected();
          }}
          onChange={(e) => table.toggleAllPageRowsSelected(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      cell: ({ row }: any) => (
        <input
          type="checkbox"
          aria-label="Rechnung für Sammelüberweisung auswählen"
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-30"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          // Ohne stopPropagation oeffnet der Klick zusaetzlich das Detail-Modal.
          onChange={row.getToggleSelectedHandler()}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        />
      ),
    } as ColumnDef<any>] : []),
    {
      id: 'important',
      header: '',
      // Ohne Label waere der Eintrag im Spalten-Menue leer (labelOf faellt auf den
      // Header-String zurueck), ausblendbar soll die Stern-Spalte ohnehin nicht sein.
      meta: { label: 'Wichtig' },
      enableHiding: false,
      cell: ({ row }) => {
        const inv = row.original;
        // Kindzeilen (Mahnung unter ihrer Rechnung) tragen das Feld nicht — die
        // Mahnung selbst ist im Mahnungen-Tab als eigene Zeile markierbar.
        if (inv.kind === 'reminder') return null;
        // Prueft Typ UND offenen Status in einem (Kundenwunsch #119):
        // bezahlt/erledigt faellt die Markierung weg.
        if (!isImportantEligible(inv)) return null;
        const marked = !!inv.is_important;
        return (
          <button
            onClick={(e) => { e.stopPropagation(); importantMutation.mutate({ invoiceId: inv.id, important: !marked }); }}
            className="p-0.5 -m-0.5 flex-shrink-0"
            title={marked ? 'Markierung „Wichtig" entfernen' : 'Als wichtige Rechnung markieren'}
          >
            <Star className={`w-4 h-4 transition-colors ${marked ? 'fill-amber-400 text-amber-500' : 'text-gray-300 hover:text-amber-400'}`} />
          </button>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const inv = row.original;
        if (inv.kind === 'reminder') return <ReminderStatusCell rem={inv} />;

        const statusCfg = STATUS_CONFIG[inv.status] || STATUS_CONFIG.new;
        const isReminder = inv.type === 'reminder';
        const hasLinkedInvoices = (inv.linked_invoice_ids?.length || 0) > 0;
        const reminderDone = isReminder && isReminderDone(inv.status);
        const isUnlinked = isReminder && !hasLinkedInvoices && !reminderDone;
        const canExpand = row.getCanExpand?.() ?? false;
        return (
          <div className="flex items-center gap-1.5">
            {canExpand && (
              <button
                onClick={(e) => { e.stopPropagation(); row.toggleExpanded(); }}
                className="text-gray-400 hover:text-gray-700 flex-shrink-0"
                title={row.getIsExpanded() ? 'Mahnungen einklappen' : 'Mahnungen anzeigen'}
              >
                {row.getIsExpanded() ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            )}
            {isReminder ? (
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  reminderDone
                    ? 'bg-green-100 text-green-700'
                    : 'bg-orange-100 text-orange-700'
                }`}>
                  {inv.status === 'resolved' ? '✓ Erledigt' : inv.status === 'paid' ? '✓ Bezahlt' : `Mahnung${inv.reminder_level ? ` St.${inv.reminder_level}` : ''}`}
                </span>
                {!reminderDone && inv.expected_invoice_count > 0 && (
                  <span className={`text-xs ${inv.linked_invoice_ids.length >= inv.expected_invoice_count ? 'text-green-600' : 'text-gray-400'}`}
                        title={`${inv.linked_invoice_ids.length} von ${inv.expected_invoice_count} im Beleg genannten Rechnungen zugeordnet`}>
                    {inv.linked_invoice_ids.length}/{inv.expected_invoice_count}
                  </span>
                )}
              </div>
            ) : (
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusCfg.color}`}>
                {statusCfg.label}
              </span>
            )}
            {/* Mahnungs-Badge an der Elternzeile: sichtbar auch im eingeklappten Zustand */}
            <ParentReminderBadge count={inv.reminder_count || 0} maxLevel={inv.reminder_max_level} />
            {isUnlinked && (
              <span className="text-red-500" title="Keiner Rechnung zugeordnet">
                <AlertTriangle className="w-3.5 h-3.5" />
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'invoice_number',
      accessorFn: (row) => row.invoice_number || '',
      header: 'Rechnungsnr.',
      cell: ({ row }) => {
        const inv = row.original;
        if (inv.kind === 'reminder') {
          return <ReminderNumberCell rem={inv} onOpenDetail={detail.open} />;
        }
        const isUnlinkedReminder =
          inv.type === 'reminder' && !(inv.linked_invoice_ids?.length > 0) && !isReminderDone(inv.status);
        return (
          <span className="text-sm font-mono">
            {inv.type === 'credit_note' && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700 mr-1.5">GS</span>
            )}
            {/* #124: von einem Mitarbeiter ausgelegt — gleiche Machart wie GS,
                nur blau. Der Name steht im Tooltip, die Spalte ist eng. */}
            {!!inv.is_employee_invoice && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 mr-1.5"
                title={inv.employee_name ? `Von ${inv.employee_name} ausgelegt` : 'Von einem Mitarbeiter ausgelegt'}
              >
                MT
              </span>
            )}
            {inv.invoice_number || '-'}
            {isUnlinkedReminder && (
              <button
                onClick={(e) => { e.stopPropagation(); setAssignReminder(inv); }}
                className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors"
              >
                <Link2 className="w-3 h-3" /> Zuordnen
              </button>
            )}
          </span>
        );
      },
    },
    {
      id: 'supplier',
      accessorFn: (row) => row.supplier_name || '',
      header: 'Lieferant',
      cell: ({ row }) => {
        const inv = row.original;
        if (inv.kind === 'reminder') return <ReminderSupplierCell rem={inv} />;
        return (
          <span className="text-sm font-medium text-gray-900">
            {inv.supplier_name || '-'}
            {/* Die Mitarbeiter-Markierung sass bis 14.08.2026 hier als lila „MA".
                Sie steht jetzt als blaues „MT" bei der Rechnungsnummer — dort, wo
                auch das GS-Badge sitzt (#124). */}
            {!!inv.duplicate_of_invoice_id && (
              <span
                className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700"
                title={`Möglicherweise doppelt erfasst — gleiche Nummer wie Invoice #${inv.duplicate_of_invoice_id}`}
              >
                Duplikat?
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: 'gross_amount',
      accessorFn: (row) => row.gross_amount ?? 0,
      header: () => <span className="block text-right">Betrag</span>,
      cell: ({ row }) => {
        const inv = row.original;
        // Kindzeile zeigt NIE den Mahnbetrag — eine Sammelmahnung erscheint unter
        // jeder ihrer Rechnungen, der Gesamtbetrag waere dort N-fach zu lesen.
        if (inv.kind === 'reminder') return <ReminderAmountCell rem={inv} />;
        // #82: Teilzahlungen wie Gutschriften behandeln — Restbetrag anzeigen
        const partialPaid = inv.status !== 'paid' ? (inv.paid_amount || 0) : 0;
        if (inv.total_credits_applied > 0 || partialPaid > 0) {
          return (
            <div className="text-right text-sm font-medium">
              <span className="text-gray-400 line-through text-xs">{formatCurrency(inv.gross_amount)}</span>
              <br />
              <span className="text-emerald-600">{formatCurrency(Math.max(0, inv.gross_amount - (inv.total_credits_applied || 0) - partialPaid))}</span>
              {partialPaid > 0 && <span className="block text-[10px] text-amber-600 font-normal">Teilzahlung</span>}
            </div>
          );
        }
        return <span className="text-sm text-right font-medium block">{formatCurrency(inv.gross_amount)}</span>;
      },
    },
    {
      id: 'invoice_date',
      accessorFn: (row) => row.invoice_date || '',
      header: 'Rechnungsdatum',
      cell: ({ row }) => {
        const inv = row.original;
        if (inv.kind === 'reminder') return <ReminderDateCell rem={inv} />;
        return <span className="text-sm text-gray-600">{formatDate(inv.invoice_date)}</span>;
      },
    },
    {
      id: 'due_date',
      // Sortiert nach dem Datum, an dem gehandelt werden muss — solange Skonto
      // zu holen ist, ist das die Skontofrist und nicht die Faelligkeit (#125).
      // Anzeige und Sortierung ziehen dieselbe Funktion, sonst steht die Liste
      // in einer anderen Reihenfolge als das, was drinsteht.
      accessorFn: (row) => (row.kind === 'reminder' ? (row.due_date || '') : getPaymentDeadline(row).sortKey),
      header: 'Fällig',
      cell: ({ row }) => {
        const inv = row.original;
        if (inv.kind === 'reminder') return <ReminderDueCell rem={inv} />;
        return <DueDateCell invoice={inv} />;
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [activeTab]);

  return (
    <div>
      <PageHeader
        title="Buchhaltung"
        description="Rechnungsverwaltung mit KI-Erkennung und Freigabe-Workflow"
        actions={
          <Button variant="outline" icon={Receipt} onClick={() => setExpenseModal(true)}>
            Ausgabenbeleg
          </Button>
        }
      />

      {/* KPI Cards — Eingangsrechnungen + Mahnungen (Ausgangsrechnungen: eigene Seite) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-card-value text-red-600">{stats?.overdue?.count || 0}</p>
                <p className="stat-card-label">Überfällig</p>
                {stats?.overdue?.total > 0 && (
                  <p className="text-xs text-red-500 mt-1">{formatCurrency(stats.overdue.total)}</p>
                )}
              </div>
              <div className="p-3 rounded-lg bg-red-50">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-card-value text-amber-600">{stats?.dueSoon?.count || 0}</p>
                <p className="stat-card-label">Fällig in 7 Tagen</p>
                {stats?.dueSoon?.total > 0 && (
                  <p className="text-xs text-amber-500 mt-1">{formatCurrency(stats.dueSoon.total)}</p>
                )}
              </div>
              <div className="p-3 rounded-lg bg-amber-50">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-card-value text-green-600">{stats?.skontoAvailable?.count || 0}</p>
                <p className="stat-card-label">Skonto nutzbar</p>
                {stats?.skontoAvailable?.savings > 0 && (
                  <p className="text-xs text-green-500 mt-1">{formatCurrency(stats.skontoAvailable.savings)} Ersparnis</p>
                )}
              </div>
              <div className="p-3 rounded-lg bg-green-50">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-card-value text-blue-600">{stats?.totalOpen?.count || 0}</p>
                <p className="stat-card-label">Offen gesamt</p>
                {stats?.totalOpen?.total > 0 && (
                  <p className="text-xs text-blue-500 mt-1">{formatCurrency(stats.totalOpen.total)}</p>
                )}
                {/* Nebenforderungen bewusst GESONDERT — nicht in die Hauptsumme
                    einrechnen, die Hauptforderung steckt schon in den Rechnungen.
                    Die „unklar"-Zahl macht nicht ermittelbare Gebuehren zur Aufgabe,
                    statt sie in einer Summe verschwinden zu lassen. */}
                {(stats?.reminderFees?.total > 0 || stats?.reminderFees?.unknown_count > 0) && (
                  <button
                    onClick={() => { setActiveTab('reminder'); setShowAllReminders(true); }}
                    className="text-xs text-orange-600 hover:text-orange-700 mt-1 block text-left"
                  >
                    + {formatCurrency(stats.reminderFees.total || 0)} Nebenforderungen
                    {stats.reminderFees.unknown_count > 0 && (
                      <span className="text-orange-500"> ({stats.reminderFees.unknown_count} unklar)</span>
                    )}
                  </button>
                )}
              </div>
              <div className="p-3 rounded-lg bg-blue-50">
                <Receipt className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </Card>
        </div>

      {/* Tabs: Eingangsrechnungen | Ausgangsrechnungen (→ eigene Seite) | Mahnungen (#2/#3) */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="border-b border-gray-200">
          <div className="flex">
            {[
              { value: 'incoming', label: 'Eingangsrechnungen' },
              { value: 'outgoing', label: 'Ausgangsrechnungen' },
              { value: 'reminder', label: 'Mahnungen' },
            ].map(tab => (
              <button
                key={tab.value}
                // Ausgangsrechnungen: eigene Seite, der Tab bleibt als gewohnter Einstieg
                onClick={() => {
                  if (tab.value === 'outgoing') { navigate('/admin/ausgangsrechnungen'); return; }
                  setActiveTab(tab.value); setStatusFilter('');
                }}
                className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.value
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="px-4 py-3">
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <div className="relative min-w-[200px] max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Lieferant, Nr. oder Schlagwort im Beleg..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Status filter */}
            <div className="flex gap-1 flex-wrap">
              {statusTabs.map(tab => (
                <button
                  key={tab.value}
                  onClick={() => setStatusFilter(tab.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    statusFilter === tab.value
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Mitarbeiter-Filter (#9) */}
            {activeTab === 'incoming' && (
              <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={showEmployeeOnly}
                  onChange={e => setShowEmployeeOnly(e.target.checked)}
                  className="rounded text-primary-500"
                />
                <span className="text-gray-600">Nur Mitarbeiter</span>
              </label>
            )}

            {/* Mahnungen-Tab ist eine Arbeitsliste. Ohne diesen Schalter waeren
                zugeordnete Mahnungen nur ueber ihre Rechnung erreichbar. */}
            {activeTab === 'reminder' && (
              <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={showAllReminders}
                  onChange={e => setShowAllReminders(e.target.checked)}
                  className="rounded text-primary-500"
                />
                <span className="text-gray-600">Auch zugeordnete Mahnungen</span>
              </label>
            )}
          </div>
        </div>
      </div>

      {activeTab === 'reminder' && !showAllReminders && (
        <div className="mb-4 text-sm text-gray-500 px-1">
          Diese Liste zeigt Mahnungen, denen <strong>keine Rechnung zugeordnet</strong> ist.
          Zugeordnete Mahnungen erscheinen als Unterzeile ihrer Rechnung im Tab „Eingangsrechnungen".
        </div>
      )}

      {/* Unmatched reminders warning — immer sichtbar, egal welcher Tab */}
      {stats?.unmatchedReminders?.count > 0 && (
        <div
          className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 flex items-center gap-3 cursor-pointer hover:bg-orange-100 transition-colors"
          onClick={() => setActiveTab('reminder')}
        >
          <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0" />
          <span className="text-sm text-orange-800">
            <strong>{stats.unmatchedReminders.count} Mahnung(en)</strong> ohne zugeordnete Rechnung — <span className="underline">Mahnungen anzeigen</span>
          </span>
          <ChevronDown className="w-4 h-4 text-orange-400 ml-auto" />
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : (
        <>
        <PaymentBatchBar
          invoices={selectedInvoices as any}
          onClear={() => setRowSelection({})}
          onMarkPaid={(ids, applySkonto) => markPaidMutation.mutate({ ids, applySkonto })}
          markPaidPending={markPaidMutation.isPending}
        />
        <DataTable
          columns={columns}
          data={effectiveRows}
          emptyText={
            activeTab === 'reminder' && !showAllReminders
              ? 'Keine unzugeordneten Mahnungen — alle sind ihrer Rechnung zugeordnet'
              : 'Keine Rechnungen gefunden'
          }
          pageSize={50}
          getSubRows={getInvoiceSubRows}
          getRowId={getInvoiceRowId}
          expanded={expandedRows}
          onExpandedChange={setExpandedRows as any}
          onRowClick={(row: any) => detail.open(row.id)}
          getRowClassName={(row: any) => {
            if (row.kind === 'reminder') return 'bg-orange-50/25 hover:bg-orange-50/50';
            // #119: wichtige Rechnungen gewinnen optisch — sie stehen ohnehin oben
            if (row.is_important && isImportantEligible(row)) return 'bg-amber-50 hover:bg-amber-100/70';
            const isReminder = row.type === 'reminder';
            if (isReminder && isReminderDone(row.status)) return 'bg-green-50/40 opacity-60';
            if (isReminder) return 'bg-orange-50/40';
            // Zeilenfarbe aus derselben Quelle wie die Fällig-Spalte — sonst ist
            // eine Zeile rot hinterlegt, während die Spalte "in 5 Tagen" zeigt.
            if (getPaymentDeadline(row).tone === 'over') return 'bg-red-50/50';
            return undefined;
          }}
          // Sammelueberweisung: nur offene Eingangsrechnungen und Gutschriften
          // sind markierbar (siehe isPayableRow).
          enableRowSelection={(row: any) => isPayableRow(row.original)}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection as any}
        />
        </>
      )}

      {/* Zuordnungs-Dialog für Mahnungen */}
      <ReminderAssignDialog
        reminder={assignReminder}
        onClose={() => setAssignReminder(null)}
        onChanged={() => {
          // deckt auch ['invoices','stats'] ab (Prefix-Invalidierung)
          invalidateInvoices();
        }}
      />

      {/* Upload Modal */}
      <Modal
        isOpen={uploadModal}
        onClose={() => { setUploadModal(false); setUploadResult(null); }}
        title="Rechnung hochladen"
        size="md"
      >
        <div className="space-y-4">
          {!uploadResult ? (
            <>
              <p className="text-sm text-gray-600">
                Laden Sie eine Rechnung (PDF oder Bild) hoch. Die KI erkennt automatisch alle relevanten Daten.
              </p>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-400 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                {uploading ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-10 h-10 text-primary-500 animate-spin" />
                    <p className="text-sm text-gray-600">KI analysiert Rechnung...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <Upload className="w-10 h-10 text-gray-400" />
                    <p className="text-sm text-gray-600">
                      PDF oder Bild hier klicken oder ablegen
                    </p>
                    <p className="text-xs text-gray-400">Max. 20 MB</p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.gif"
                onChange={handleFileUpload}
                className="hidden"
              />
            </>
          ) : (
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-800">
                  {uploadResult.type === 'reminder'
                    ? 'Mahnung erkannt'
                    : 'Rechnung erfolgreich analysiert'
                  }
                </span>
              </div>

              {uploadResult.duplicate_warning && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <strong>Mögliches Duplikat:</strong>{' '}
                    {uploadResult.duplicate_warning.supplier_name} / Rechnungsnr.{' '}
                    {uploadResult.duplicate_warning.invoice_number} existiert bereits
                    {uploadResult.duplicate_warning.gross_amount != null && (
                      <> über {formatCurrency(uploadResult.duplicate_warning.gross_amount)}</>
                    )}{' '}
                    (Invoice #{uploadResult.duplicate_warning.existing_id}). Die neue Rechnung wurde
                    trotzdem angelegt und in der Liste als „Duplikat?" markiert — bitte prüfen und
                    ggf. eine der beiden löschen.
                  </div>
                </div>
              )}

              {uploadResult.data && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  {uploadResult.data.supplier_name && (
                    <div><strong>{uploadResult.type === 'outgoing' ? 'Empfänger' : 'Lieferant'}:</strong> {uploadResult.data.recipient_name || uploadResult.data.supplier_name}</div>
                  )}
                  {uploadResult.data.invoice_number && (
                    <div><strong>Rechnungsnr.:</strong> {uploadResult.data.invoice_number}</div>
                  )}
                  {uploadResult.data.gross_amount != null && (
                    <div><strong>Betrag:</strong> {formatCurrency(uploadResult.data.gross_amount)}</div>
                  )}
                  {uploadResult.data.due_date && (
                    <div><strong>Fällig:</strong> {formatDate(uploadResult.data.due_date)}</div>
                  )}
                  {uploadResult.data.description && (
                    <div><strong>Beschreibung:</strong> {uploadResult.data.description}</div>
                  )}
                  {uploadResult.data.skonto_percent && (
                    <div><strong>Skonto:</strong> {uploadResult.data.skonto_percent}% bei Zahlung innerhalb {uploadResult.data.skonto_days} Tagen</div>
                  )}
                </div>
              )}

              <div className="flex justify-end">
                <Button variant="primary" onClick={() => { setUploadModal(false); setUploadResult(null); }}>
                  Schließen
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Expense Claim Modal */}
      <Modal
        isOpen={expenseModal}
        onClose={() => setExpenseModal(false)}
        title="Ausgabenbeleg erfassen"
        size="md"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lieferant / Geschäft *</label>
              <input
                value={expenseData.supplier_name}
                onChange={e => setExpenseData(d => ({ ...d, supplier_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                placeholder="z.B. Bauhaus, Tankstelle..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mitarbeiter</label>
              <input
                value={expenseData.employee_name}
                onChange={e => setExpenseData(d => ({ ...d, employee_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                placeholder="Wer hat bezahlt?"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
            <input
              value={expenseData.description}
              onChange={e => setExpenseData(d => ({ ...d, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
              placeholder="Was wurde gekauft?"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Brutto-Betrag *</label>
              <input
                type="number"
                step="0.01"
                value={expenseData.gross_amount}
                onChange={e => setExpenseData(d => ({ ...d, gross_amount: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                placeholder="0,00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Netto (auto)</label>
              <p className="px-3 py-2 bg-gray-50 rounded-lg text-sm">
                {expenseData.gross_amount ? formatCurrency(parseFloat(expenseData.gross_amount) / 1.19) : '-'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">MwSt. (auto)</label>
              <p className="px-3 py-2 bg-gray-50 rounded-lg text-sm">
                {expenseData.gross_amount ? formatCurrency(parseFloat(expenseData.gross_amount) - parseFloat(expenseData.gross_amount) / 1.19) : '-'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Datum</label>
              <input
                type="date"
                value={expenseData.invoice_date}
                onChange={e => setExpenseData(d => ({ ...d, invoice_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kategorie</label>
              <select
                value={expenseData.category}
                onChange={e => setExpenseData(d => ({ ...d, category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Bitte wählen</option>
                {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notizen</label>
            <textarea
              value={expenseData.notes}
              onChange={e => setExpenseData(d => ({ ...d, notes: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
              placeholder="Optional..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Beleg (optional)</label>
            <input
              ref={expenseFileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
            />
            <p className="text-xs text-gray-400 mt-1">Foto von Kassenzettel / Quittung</p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setExpenseModal(false)}>Abbrechen</Button>
            <Button variant="primary" onClick={submitExpense} disabled={expenseSaving}>
              {expenseSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Beleg erfassen
            </Button>
          </div>
        </div>
      </Modal>

      {/* Detail-Modal — Fullscreen-Split: links der Beleg, rechts die Daten.
          Der gesamte Inhalt steckt jetzt in ./buchhaltung/InvoiceDetailModal. */}
      <InvoiceDetailModal detail={detail} onAssign={setAssignReminder} />

      {/* Zuordnungs-Dialog — MUSS nach dem Detail-Modal stehen: beide nutzen
          .modal-backdrop mit z-50, bei Gleichstand gewinnt die spaetere DOM-Position.
          Davor lag der Dialog unsichtbar hinter dem Detail-Modal. */}
      <ReminderAssignDialog
        reminder={assignReminder}
        onClose={() => setAssignReminder(null)}
        onChanged={async () => {
          invalidateInvoices();
          await detail.reload();
        }}
      />
    </div>
  );
};

export default Buchhaltung;
